import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import styles from './AddNewBanking.module.css';
import { v4 as uuidv4 } from 'uuid';
import { STOCK_TYPES } from '../common/stockType';

function AddNewBanking(props) {
    const { t } = useTranslation();

    // 유틸리티 함수
    const sharpToHash = str => str ? str.replace('#', 'hashmark') : str;
    const hashToSharp = str => str ? str.replace('hashmark', '#') : str;

    // 1. Props 처리 (부모에 따라 닫기 함수와 초기값이 다름)
    // BankingForm에서는 onCancel, BankingMain에서는 onClose를 사용
    const handleClose = () => {
        if (props.onCancel) props.onCancel();
        else if (props.onClose) props.onClose();
    };

    // Props로 넘어온 데이터가 있으면 고정값(ReadOnly), 없으면 입력 가능
    const isFixedPatient = !!props.patientCode;
    const isFixedKey = !!props.tmpKey;

    const [lineOptions, setLineOptions] = useState([]);
    const [occupiedPositions, setOccupiedPositions] = useState([]);

    // 2. Form 상태 초기화
    const [formData, setFormData] = useState({
        master: '0',
        // props가 있으면 그 값 사용, 없으면 빈 문자열 (BankingMain용)
        patientCode: props.patientCode || '',
        organoidInfoKey: props.tmpKey ? sharpToHash(props.tmpKey) : '',
        labCode: '',
        passageNo: '',
        keeper: '',
        freezingMedia: '',
        freezingDate: new Date().toISOString().split('T')[0],
        domePerVialOrCellNumber: '',
        cultureMedia: '',
        mycoplasma: '',
        thawingNo: '',
        annotation: '',
        exporter: '',
        exportDate: '',
        notes: '',
        vessels: [{ vessel: '', line: '', box: '', position: '' }]
    });

    // 3. 마운트 시 기초 데이터 로딩 (Line List, Occupied Positions)
    useEffect(() => {
        // 라인 리스트
        fetch('/api/getLineList', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(res => res.json())
            .then(data => setLineOptions(data))
            .catch(err => console.error('라인 리스트 조회 실패', err));

        // 점유된 위치 리스트
        fetch('/api/getOccupiedPositions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(res => res.json())
            .then(data => setOccupiedPositions(data))
            .catch(err => console.error('사용 위치 리스트 조회 실패', err));
    }, []);

    // 4. Organoid Info Key 변경 감지 및 추가 정보 로드
    // (BankingMain에서 직접 키를 입력했을 때도 동작함)
    useEffect(() => {
        if (props.parentTime) return;
        if (formData.organoidInfoKey) {
            fetch('/api/getBankingInfoLimitOne', {
                method: 'post',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ param: formData.organoidInfoKey }),
            })
                .then(response => response.json())
                .then(data => {
                    if (data && data[0]) {
                        setFormData(prev => ({
                            ...prev,
                            // 이미 입력된 값이 있다면 덮어쓰지 않도록 주의하거나, DB값으로 강제 업데이트
                            labCode: data[0].LabCode || prev.labCode,
                            patientCode: isFixedPatient ? prev.patientCode : (data[0].PatientCode || prev.patientCode),
                            // 필요한 경우 Keeper, Media 등도 여기서 자동 채움 가능
                        }));
                    }
                })
                .catch(err => console.error('기본 정보 로드 실패', err));
        }
    }, [formData.organoidInfoKey, isFixedPatient, props.parentTime]);

    // 4-2. [추가] 자식 스탁 추가 시, 부모 time으로 부모 값 상속
    useEffect(() => {
        if (!props.parentTime) return;
        fetch('/api/getBankingInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: props.parentTime }),
        })
            .then(res => res.json())
            .then(data => {
                const parent = data && data[0];
                if (!parent) return;
                setFormData(prev => ({
                    ...prev,
                    labCode: parent.LabCode || prev.labCode,
                    passageNo: parent.PassageNo || prev.passageNo,
                    keeper: parent.Keeper || prev.keeper,
                    freezingMedia: parent.FreezingMedia || prev.freezingMedia,
                    cultureMedia: parent.CultureMedia || prev.cultureMedia,
                    mycoplasma: parent.Mycoplasma || prev.mycoplasma,
                    domePerVialOrCellNumber: parent.domePerVialOrCellNumber || prev.domePerVialOrCellNumber,
                }));
            })
            .catch(err => console.error('부모 스탁 정보 조회 실패', err));
    }, [props.parentTime]);

    // 5. 핸들러 함수들
    const handleChange = e => {
        const { name, value } = e.target;
        // Key 입력 시에는 '#' 변환 로직 적용 가능 (여기서는 있는 그대로 입력받고 저장 시 변환)
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 오가노이드 키 입력 전용 핸들러 (입력은 #으로 보여주고 저장은 hashmark로 하려면 변환 필요)
    // 여기서는 사용자가 '#'을 입력하면 내부적으로 'hashmark'로 저장하고, 보여줄 땐 '#'으로 변환
    const handleKeyChange = e => {
        const val = e.target.value;
        setFormData(prev => ({
            ...prev,
            organoidInfoKey: sharpToHash(val)
        }));
    };

    // [2026-09-09] Master 체크박스 → MCB/WCB/ARB 3지선다
    const handleMasterChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Vessel 관련 핸들러
    const handleVesselChange = (idx, e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const vessels = [...prev.vessels];
            vessels[idx][name] = value;
            if (name === 'vessel') {
                vessels[idx].line = '';
            }
            return { ...prev, vessels };
        });
    };

    const addVesselGroup = () => {
        setFormData(prev => {
            const vessels = [...prev.vessels];
            const last = vessels.length
                ? vessels[vessels.length - 1]
                : { vessel: '', line: '', box: '', position: '' };
            vessels.push({
                vessel: last.vessel,
                line: last.line,
                box: last.box,
                position: ''
            });
            return { ...prev, vessels };
        });
    };

    const removeVesselGroup = idx => {
        setFormData(prev => {
            const vessels = [...prev.vessels];
            vessels.splice(idx, 1);
            return { ...prev, vessels };
        });
    };

    // 자동 할당 로직
    const assignPosition = (idx) => {
        const { vessel, line, box } = formData.vessels[idx];
        if (!vessel || !line || !box) {
            alert(t('먼저 Vessel, Line, Box를 선택해주세요.'));
            return;
        }

        const norm = (s) => String(s ?? '').trim().toUpperCase();
        const boxKey = norm(box);

        const takenFromDB = occupiedPositions
            .filter(p =>
                String(p.Vessel) === vessel &&
                p.Line === line &&
                norm(p.Box) === boxKey
            )
            .map(p => Number(p.Position));

        const takenInForm = formData.vessels
            .filter((v, i) =>
                i !== idx &&
                v.vessel === vessel &&
                v.line === line &&
                v.position !== '' &&
                norm(v.box) === boxKey
            )
            .map(v => Number(v.position));

        const taken = new Set([...takenFromDB, ...takenInForm]);
        let available = 1;
        while (available <= 100 && taken.has(available)) {
            available++;
        }
        if (available > 100) {
            alert(t('사용 가능한 Position이 없습니다.'));
            return;
        }

        setFormData(prev => {
            const vessels = [...prev.vessels];
            vessels[idx].position = String(available);
            return { ...prev, vessels };
        });
    };

    // 제출 핸들러
    const handleSubmitClick = async () => {
        const { vessels } = formData;

        // Validation
        if (!formData.organoidInfoKey) {
            alert(t("Organoid Code는 필수입니다."));
            return;
        }
        if (!formData.patientCode) {
            alert(t("Patient Code는 필수입니다."));
            return;
        }

        // 중복 검사
        const seen = new Set();
        for (let v of vessels) {
            const { vessel, line, box, position } = v;
            if (!vessel || !line || !box || position === '' || position === '0') continue;
            const key = `${vessel}||${line}||${box}||${position}`;
            if (seen.has(key)) {
                alert(t('입력 폼 내에 중복된 위치(Vessel/Line/Box/Position)가 있습니다.'));
                return;
            }
            seen.add(key);
        }

        const needBundle = vessels.length > 1;
        const bundleId = needBundle ? uuidv4() : undefined;
        // DB 저장용 키는 'hashmark' 형태여야 함 (이미 상태는 hashmark 형태지만 한번 더 확인)
        const orgKey = sharpToHash(formData.organoidInfoKey);

        const commonData = {
            PatientCode: formData.patientCode,
            Organoid_Info_Key: orgKey,
            LabCode: formData.labCode,
            PassageNo: formData.passageNo,
            Keeper: formData.keeper,
            FreezingMedia: formData.freezingMedia,
            FreezingDate: formData.freezingDate,
            domePerVialOrCellNumber: formData.domePerVialOrCellNumber,
            CultureMedia: formData.cultureMedia,
            Mycoplasma: formData.mycoplasma,
            thawingNo: formData.thawingNo,
            annotation: formData.annotation,
            Exporter: formData.exporter,
            ExporteDate: formData.exportDate,
            Master: formData.master,
            draftOpinion: formData.notes,
            // [중요] 부모로부터 받은 ParentTime(부모 스탁의 시간값) 추가
            ParentTime: props.parentTime || null,
        };

        const messages = [];
        let sawConflict = false;

        for (const v of vessels) {
            // [복구] 기존 코드와 동일한 Payload 구조 생성
            // formData 전체를 펼치고, vessels 배열 안에 현재 vessel 객체(v)를 하나 넣어 보냅니다.
            const singleParam = {
                ...formData,
                organoidInfoKey: orgKey,
                vessels: [v], // 서버가 이 배열 구조를 기대함
                ...(needBundle && { bundleId }),
                // [추가] 부모 시간 정보 (자식 스탁 추가 시) - camelCase로 추가
                ParentCode: props.parentTime || null
            };

            try {
                const res = await fetch('/api/draftAddBankingInfo', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ param: singleParam })
                });
                const data = await res.json();
                if (res.status === 409) {
                    sawConflict = true;
                    messages.push(`⚠️ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message}`);
                } else if (!res.ok) {
                    messages.push(`❌ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message || t('서버 오류')}`);
                } else {
                    messages.push(`✅ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${data.message}`);
                }
            } catch {
                messages.push(`⚠️ [${v.vessel}/${v.line}/${v.box}/${v.position}] → ${t('요청 실패')}`);
            }
        }

        alert(messages.join('\n'));
        if (!sawConflict) {
            handleClose(); // 성공 시 닫기
        }
    };

    return (
        <div className={styles.modalBackground} onClick={handleClose}>
            <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
                <button type="button" className={styles.closeButton} onClick={handleClose}>&times;</button>
                <div className={styles.title}>{t('Add New Organoid Banking(Stock)')}</div>

                <div className={styles.submitGroup}>
                    <div className={styles.formLabel}>Stock</div>
                    <select
                        name="master"
                        value={formData.master}
                        onChange={handleMasterChange}
                        className={styles.rightValue}
                    >
                        {STOCK_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.full}</option>
                        ))}
                    </select>

                    <div className={styles.formLabel}>Patient Code</div>
                    <input
                        type="text"
                        name="patientCode"
                        value={formData.patientCode}
                        onChange={handleChange}
                        readOnly={isFixedPatient} // BankingForm에서 오면 ReadOnly, 아니면 입력 가능
                        className={`${styles.rightValue} ${isFixedPatient ? styles.readOnly : ''}`}
                        placeholder={!isFixedPatient ? t('입력하세요') : ''}
                    />

                    <div className={styles.formLabel}>Organoid Code</div>
                    <input
                        type="text"
                        name="organoidInfoKeyDisplay"
                        value={hashToSharp(formData.organoidInfoKey)}
                        onChange={handleKeyChange}
                        readOnly={isFixedKey} // BankingForm에서 오면 ReadOnly, 아니면 입력 가능
                        className={`${styles.rightValue} ${isFixedKey ? styles.readOnly : ''}`}
                        placeholder={!isFixedKey ? t('입력하세요') : ''}
                    />

                    <div className={styles.formLabel}>Lab Code</div>
                    <input
                        type="text"
                        name="labCode"
                        value={formData.labCode}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Passage No.</div>
                    <input
                        type="text"
                        name="passageNo"
                        value={formData.passageNo}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Keeper</div>
                    <input
                        type="text"
                        name="keeper"
                        value={formData.keeper}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Freezing Media</div>
                    <input
                        list="freezingMediaList"
                        name="freezingMedia"
                        value={formData.freezingMedia}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />
                    <datalist id="freezingMediaList">
                        <option value="Recovery Cell Culture Freezing Medium" />
                    </datalist>

                    <div className={styles.formLabel}>Freezing Date</div>
                    <input
                        type="date"
                        name="freezingDate"
                        value={formData.freezingDate}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Dome/Vial or Cell Number</div>
                    <input
                        type="text"
                        name="domePerVialOrCellNumber"
                        value={formData.domePerVialOrCellNumber}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Culture Media</div>
                    <input
                        type="text"
                        name="cultureMedia"
                        value={formData.cultureMedia}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Mycoplasma</div>
                    <select
                        name="mycoplasma"
                        value={formData.mycoplasma}
                        onChange={handleChange}
                        className={styles.rightValue}
                    >
                        <option value="">Select</option>
                        <option value="NA">NA</option>
                        <option value="Negative">Negative</option>
                        <option value="Positive">Positive</option>
                    </select>

                    <div className={styles.formLabel}>Thawing No.</div>
                    <input
                        type="text"
                        name="thawingNo"
                        value={formData.thawingNo}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Annotation</div>
                    <textarea
                        name="annotation"
                        value={formData.annotation}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Exporter</div>
                    <input
                        type="text"
                        name="exporter"
                        value={formData.exporter}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />

                    <div className={styles.formLabel}>Export Date</div>
                    <input
                        type="date"
                        name="exportDate"
                        value={formData.exportDate}
                        onChange={handleChange}
                        className={styles.rightValue}
                    />
                </div>

                {formData.vessels.map((v, idx) => (
                    <div className={styles.positionBox} key={idx}>
                        <span className={styles.positionNumber}>{idx + 1}</span>
                        <div className={styles.vesselGroup}>
                            <div className={styles.formLabel}>Storage</div>
                            <input
                                type="number"
                                name="vessel"
                                placeholder={t('1~4')}
                                value={v.vessel ?? ''}
                                onChange={e => {
                                    const val = e.target.value;
                                    if (val === '' || (/^[1-4]$/.test(val))) handleVesselChange(idx, e);
                                }}
                                min="1" max="4"
                                className={styles.rightValue}
                            />

                            <div className={styles.formLabel}>Line</div>
                            <select
                                name="line"
                                value={v.line}
                                onChange={e => handleVesselChange(idx, e)}
                                className={styles.rightValue}
                            >
                                <option value="" disabled>{t('Select')}</option>
                                {lineOptions
                                    .filter(opt => String(opt.vesselNo) === v.vessel)
                                    .map(opt => (
                                        <option key={opt.lineName} value={opt.lineName}>
                                            {opt.lineName}
                                        </option>
                                    ))
                                }
                            </select>

                            <div className={styles.formLabel}>Box</div>
                            <select
                                name="box"
                                value={v.box}
                                onChange={e => handleVesselChange(idx, e)}
                                required
                                className={styles.rightValue}
                            >
                                <option value="" disabled>{t('Select Box')}</option>
                                {Array.from({ length: 13 }, (_, i) => (
                                    <option key={i} value={`BOX${i + 1}`}>{`BOX${i + 1}`}</option>
                                ))}
                            </select>

                            <div className={styles.formLabel}>Position</div>
                            <input
                                type="number"
                                name="position"
                                value={v.position ?? ''}
                                min="0" max="100"
                                placeholder={t('1~100')}
                                className={styles.rightValue}
                                onChange={e => {
                                    const n = Number(e.target.value);
                                    if (e.target.value === '' || (n >= 0 && n <= 100)) handleVesselChange(idx, e);
                                }}
                            />
                            <button type="button" onClick={() => assignPosition(idx)} className={styles.autoButton}>{t('자동')}</button>
                            <button type="button" onClick={() => removeVesselGroup(idx)} className={styles.removeButton}>-</button>
                        </div>
                    </div>
                ))}

                <button type="button" onClick={addVesselGroup} className={styles.addButton}>+</button>

                <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t('Draft your opinion here.')}
                    className={styles.draftOpinion}
                />

                <div className={styles.submitDiv}>
                    <button type="button" className={styles.submitButton} onClick={handleSubmitClick}>{t('Submit')}</button>
                    <button type="button" className={styles.cancelButton} onClick={handleClose}>{t('Cancel')}</button>
                </div>
            </div>
        </div>
    );
}

export default AddNewBanking;